import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { fetchAccountMe, patchAccountMe } from "../api/client.js";
import { hasMarketplaceJwtCredentials, clearWicoopSession } from "../lib/wicoopProfileStorage.js";

const OFFER_URL = "https://wicoop.ru/cashback-offer";
const ACCENT = "#00C853";

/**
 * Гейт согласия с офертой. Показывается поверх всего, если авторизованный
 * покупатель ещё не принял оферту (offer_accepted === false). Пока не примет —
 * пользоваться приложением нельзя. Покрывает и новых (после регистрации), и
 * существующих пользователей. Содержит обязательную галочку.
 */
export default function OfferConsentGate() {
  const [needConsent, setNeedConsent] = useState(false);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const settledRef = useRef(false);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (settledRef.current) return;
      if (!hasMarketplaceJwtCredentials()) {
        setNeedConsent(false);
        return;
      }
      try {
        const me = await fetchAccountMe();
        if (cancelled) return;
        // Гейт показываем только если сервер ЯВНО вернул offer_accepted === false.
        // Если поле отсутствует (старый API) — не запираем.
        if (me && me.offer_accepted === false) {
          setNeedConsent(true);
        } else {
          setNeedConsent(false);
          settledRef.current = true;
        }
      } catch {
        // не авторизованы / ошибка сети — не мешаем
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  if (!needConsent) return null;

  async function accept() {
    if (!checked || busy) return;
    setBusy(true);
    setError("");
    try {
      await patchAccountMe({ offer_accepted: true });
      settledRef.current = true;
      setNeedConsent(false);
    } catch {
      setError("Не удалось сохранить. Попробуйте ещё раз.");
    } finally {
      setBusy(false);
    }
  }

  function logout() {
    try {
      clearWicoopSession();
    } catch {
      /* ignore */
    }
    settledRef.current = true;
    setNeedConsent(false);
    window.location.href = "/";
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.inner}>
        <div style={styles.topRow}>
          <button type="button" onClick={logout} style={styles.logout}>
            Выйти
          </button>
        </div>
        <h1 style={styles.title}>Примите условия оферты</h1>
        <p style={styles.subtitle}>
          Чтобы продолжить пользоваться приложением, подтвердите согласие с условиями оферты.
        </p>
        <label style={styles.row}>
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => {
              setChecked(e.target.checked);
              setError("");
            }}
            style={styles.checkbox}
          />
          <span style={styles.consentText}>
            Я принимаю условия{" "}
            <a href={OFFER_URL} target="_blank" rel="noopener noreferrer" style={styles.link}>
              оферты
            </a>{" "}
            и политику конфиденциальности.
          </span>
        </label>
        {error ? <p style={styles.error}>{error}</p> : null}
        <button
          type="button"
          onClick={accept}
          disabled={!checked || busy}
          style={{ ...styles.button, opacity: !checked || busy ? 0.5 : 1 }}
        >
          {busy ? "Сохраняем…" : "Принять и продолжить"}
        </button>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: "var(--tg-theme-bg-color, #0f1216)",
    color: "var(--tg-theme-text-color, #e7e9ea)",
    padding: "20px",
    overflowY: "auto",
  },
  inner: { maxWidth: 480, margin: "0 auto", width: "100%" },
  topRow: { display: "flex", justifyContent: "flex-end", paddingTop: 8, paddingBottom: 8 },
  logout: { background: "none", border: "none", color: "inherit", opacity: 0.6, fontSize: 14, fontWeight: 600, cursor: "pointer" },
  title: { fontSize: 26, fontWeight: 800, margin: "12px 0 8px" },
  subtitle: { fontSize: 14, opacity: 0.7, margin: "0 0 26px", lineHeight: 1.45 },
  row: { display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" },
  checkbox: { width: 22, height: 22, accentColor: ACCENT, marginTop: 1, flexShrink: 0 },
  consentText: { fontSize: 14, lineHeight: 1.45 },
  link: { color: ACCENT, fontWeight: 600 },
  error: { color: "#e5484d", fontSize: 13, marginTop: 12 },
  button: {
    marginTop: 22,
    width: "100%",
    padding: "15px 16px",
    border: "none",
    borderRadius: 14,
    background: ACCENT,
    color: "#04210f",
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
  },
};
